import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  useDroppable,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Typography } from '@mui/material';
import { getExerciseDisplay } from '@strength/domain-workout';
import { targetSortOrderByFn } from '@strength/utils';
import { MdDragIndicator } from '@teamworksdev/icons';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../components/utils';
import { NEW_EXERCISE_WORKOUT_ID } from './TabletCreateEditExerciseSheet';
import { ExerciseOrder, ExerciseSectionEntry, SupersetEntry } from './types';

type ExerciseOrderInputProps = {
  onChange: (value: ExerciseOrder[]) => void;
  value: ExerciseOrder[];
};

export const ExerciseOrderInput = ({
  onChange,
  value,
}: ExerciseOrderInputProps) => {
  const [activeExercise, setActiveExercise] = useState<ExerciseSectionEntry>();

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setActiveExercise(undefined);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    const exercise = getExerciseById(active.id.toString());
    if (exercise) setActiveExercise(exercise);
  };

  // handle sort order between sections
  const handleDragOver = ({ active, over, delta }: DragOverEvent) => {
    if (!over || active.id === over.id) return;

    const entryToAdd = value
      .flatMap((section) => section.entries)
      .find((entry) => entry.workoutExerciseId === active.id);
    if (!entryToAdd) return;

    const {
      workoutSupersetId: overWorkoutSupersetId,
      sectionId: overWorkoutSectionId,
      exerciseSortOrder: overExerciseSortOrder,
      supersetSortOrder: overSupersetSortOrder,
    } = over.data?.current || {};

    const {
      supersetSortOrder: activeSupersetSortOrder,
      exerciseSortOrder: activeExerciseSortOrder,
    } = active.data?.current || {};

    const targetSection = value.find(
      (section) => section.workoutSectionId === overWorkoutSectionId,
    );
    if (!targetSection) return;

    const sortOrder: {
      exerciseSortOrder: number;
      supersetSortOrder?: number;
    } = {
      exerciseSortOrder: overExerciseSortOrder,
    };

    const supersetsInSection = getSupersets(targetSection);
    const sectionIds = value.flatMap((section) => section.workoutSectionId);

    // if the dragged over the section container, don't do anything
    // a.k.a wait till the user drags over an exercise or superset
    if (!sectionIds.includes(over.id.toString())) {
      if (
        overWorkoutSupersetId &&
        !supersetsInSection.some((s) => s.workoutSupersetId === over.id)
      ) {
        // handle ordering within a superset
        sortOrder.supersetSortOrder = targetSortOrderByFn(
          targetSection.entries.filter(
            (entry) => entry.workoutSupersetId === overWorkoutSupersetId,
          ),
          (n) => n.supersetSortOrder ?? 0,
          overSupersetSortOrder ?? 0,
          overSupersetSortOrder > activeSupersetSortOrder ? 'after' : 'before',
        );
      } else {
        // handle exercises
        // a superset is treated as a single exercise
        const uniqueExercises = getUniqueExercises(targetSection.entries);

        // when moving between supersets and exercises
        // knowing the direction of the movement, gives a better UX to place before or after
        const direction = delta.y < 0 ? 'up' : 'down';
        const sortOrderComparison =
          direction === 'up'
            ? overExerciseSortOrder > activeExerciseSortOrder
            : overExerciseSortOrder >= activeExerciseSortOrder;

        const position = sortOrderComparison ? 'after' : 'before';
        sortOrder.exerciseSortOrder = targetSortOrderByFn(
          uniqueExercises,
          (n) => n.exerciseSortOrder,
          overExerciseSortOrder,
          position,
        );
        sortOrder.supersetSortOrder = undefined;
      }
    }

    const newEntryValue = {
      ...entryToAdd,
      ...sortOrder,
      workoutSupersetId: overWorkoutSupersetId || undefined,
    };

    // if no sortOrder put exercise at the end of the section
    if (!newEntryValue.exerciseSortOrder) {
      const highestSortOrder = Math.min(
        ...targetSection.entries.map((entry) => entry.exerciseSortOrder),
      );
      newEntryValue.exerciseSortOrder = highestSortOrder - 1;
    }

    const updatedValues = value.map((section) => ({
      ...section,
      entries: [
        ...section.entries.filter(
          (entry) => entry.workoutExerciseId !== active.id,
        ),
        ...(section.workoutSectionId === overWorkoutSectionId
          ? [newEntryValue]
          : []),
      ],
    }));

    onChange(updatedValues);
  };

  const getSupersets = (section: ExerciseOrder) => {
    const uniqueSupersetIds = new Set<string>();

    return section.entries
      .filter((entry) => {
        if (
          entry.workoutSupersetId &&
          !uniqueSupersetIds.has(entry.workoutSupersetId)
        ) {
          uniqueSupersetIds.add(entry.workoutSupersetId);
          return true;
        }
        return false;
      })
      .map((entry) => ({
        workoutSupersetId: entry.workoutSupersetId ?? '',
        exerciseSortOrder: entry.exerciseSortOrder,
      }));
  };

  const getUniqueExercises = (entries: ExerciseSectionEntry[]) => {
    const uniqueEntries: ExerciseSectionEntry[] = [];
    entries.forEach((entry) => {
      if (
        entry.workoutSupersetId &&
        !uniqueEntries.some(
          (e) => e.workoutSupersetId === entry.workoutSupersetId,
        )
      ) {
        uniqueEntries.push(entry);
      } else if (!entry.workoutSupersetId) {
        uniqueEntries.push(entry);
      }
    });
    return uniqueEntries;
  };

  const getExerciseById = (id: string) => {
    return value
      .flatMap((section) => section.entries)
      .find((entry) => entry.workoutExerciseId === id);
  };

  const computeDnDItems = useCallback((section: ExerciseOrder) => {
    const supersets = getSupersets(section);
    const exercises = section.entries.filter(
      (entry) => !entry.workoutSupersetId,
    );
    const sectionItems = [...supersets, ...exercises].sort(
      (a, b) => a.exerciseSortOrder - b.exerciseSortOrder,
    );

    const items = sectionItems.map((e) =>
      'workoutExerciseId' in e
        ? e.workoutExerciseId ?? ''
        : e.workoutSupersetId ?? '',
    );

    return { sectionItems, items };
  }, []);

  const getSupersetData = useCallback(
    (
      section: ExerciseOrder,
      supersetOrExercise: ExerciseSectionEntry | SupersetEntry,
    ) => {
      const supersetIds = new Set(
        section.entries
          .filter((entry) => entry.workoutSupersetId)
          .map((entry) => entry.workoutSupersetId),
      );

      if (supersetIds.has(supersetOrExercise.workoutSupersetId)) {
        const workoutSupersetId = supersetOrExercise.workoutSupersetId;
        if (!workoutSupersetId) return null;

        const supersetExercises = section.entries
          .filter((entry) => entry.workoutSupersetId === workoutSupersetId)
          .sort(
            (a, b) => (a.supersetSortOrder ?? 0) - (b.supersetSortOrder ?? 0),
          );

        return { workoutSupersetId, supersetExercises };
      }

      return null;
    },
    [],
  );

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-gray-300 p-4">
      <DndContext
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        modifiers={[restrictToVerticalAxis]}
      >
        {value.map((section) => {
          const { sectionItems, items } = computeDnDItems(section);

          return (
            <SectionContainer
              key={section.workoutSectionId}
              section={section}
              items={items}
            >
              <div className="flex flex-col gap-2">
                {sectionItems.map((supersetOrExercise) => {
                  const supersetData = getSupersetData(
                    section,
                    supersetOrExercise,
                  );
                  // render superset container if superset data is found
                  if (supersetData) {
                    const { workoutSupersetId, supersetExercises } =
                      supersetData;
                    return (
                      <SupersetContainer
                        key={`${section.workoutSectionId}-${workoutSupersetId}`}
                        supersetId={workoutSupersetId}
                        supersetExercises={supersetExercises}
                        sectionId={section.workoutSectionId}
                        sortOrder={supersetOrExercise.exerciseSortOrder}
                      />
                    );
                  }

                  // render exercise item if superset data is not found
                  const exercise =
                    'workoutExerciseId' in supersetOrExercise
                      ? getExerciseById(supersetOrExercise.workoutExerciseId)
                      : undefined;
                  if (!exercise) return;
                  return (
                    <ExerciseItem
                      key={`${section.workoutSectionId}-${exercise?.workoutExerciseId}`}
                      exercise={exercise}
                      sectionId={section.workoutSectionId}
                    />
                  );
                })}
              </div>
            </SectionContainer>
          );
        })}
        <DragOverlay>
          {activeExercise ? (
            <Item
              exercise={activeExercise}
              key={`drag-overlay-${activeExercise.workoutExerciseId}`}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

const SupersetContainer = ({
  supersetId,
  supersetExercises,
  sectionId,
  sortOrder,
}: {
  supersetId: string;
  supersetExercises: ExerciseSectionEntry[];
  sectionId: string;
  sortOrder: number;
}) => {
  const { setNodeRef } = useDroppable({
    id: supersetId,
    data: {
      exerciseSortOrder: sortOrder,
      sectionId,
    },
  });

  const items = supersetExercises.map((exercise) => exercise.workoutExerciseId);

  return (
    <SortableItem id={supersetId} disabled={true}>
      <div ref={setNodeRef}>
        <div className="flex flex-col gap-2 px-4 pb-4 pt-3 border border-gray-200 rounded-md">
          <Typography variant="captionMedium" className="text-black/60">
            Superset
          </Typography>
          <SortableContext id={supersetId} items={items} disabled={true}>
            {supersetExercises.map((exercise) => (
              <ExerciseItem
                key={`${sectionId}-${supersetId}-${exercise.workoutExerciseId}`}
                exercise={exercise}
                sectionId={sectionId}
              />
            ))}
          </SortableContext>
        </div>
      </div>
    </SortableItem>
  );
};

const SectionContainer = ({
  section,
  items,
  children,
}: {
  section: ExerciseOrder;
  items: string[];
  children: React.ReactNode;
}) => {
  const { setNodeRef } = useDroppable({
    id: section.workoutSectionId,
    data: {
      sectionId: section.workoutSectionId,
    },
  });
  return (
    <SortableItem id={section.workoutSectionId} disabled={true}>
      <div ref={setNodeRef}>
        <div className="flex flex-col gap-2 pb-4 pt-3 rounded-md">
          <Typography variant="body2Semibold">{section.sectionName}</Typography>
          <SortableContext
            id={section.workoutSectionId}
            items={items}
            strategy={verticalListSortingStrategy}
          >
            {children}
          </SortableContext>
        </div>
      </div>
    </SortableItem>
  );
};

const checkIfSelectedExercise = (exercise: ExerciseSectionEntry) => {
  return exercise.workoutExerciseId === NEW_EXERCISE_WORKOUT_ID;
};

const ExerciseItem = ({
  exercise,
  sectionId,
}: {
  exercise: ExerciseSectionEntry;
  sectionId: string;
}) => {
  const isSelectedExercise = checkIfSelectedExercise(exercise);
  const data = {
    workoutExerciseId: exercise.workoutExerciseId,
    exerciseSortOrder: exercise.exerciseSortOrder,
    supersetSortOrder: exercise.supersetSortOrder ?? undefined,
    workoutSupersetId: exercise.workoutSupersetId ?? undefined,
    sectionId,
  };

  return (
    <SortableItem
      id={exercise.workoutExerciseId}
      data={data}
      disabled={!isSelectedExercise}
    >
      <Item
        exercise={exercise}
        key={`${sectionId}-${exercise.workoutExerciseId}`}
      />
    </SortableItem>
  );
};

const SortableItem = ({
  id,
  children,
  data,
  disabled,
}: {
  id: string;
  children: React.ReactNode;
  data?: {
    workoutExerciseId: string;
    workoutSupersetId?: string;
    exerciseSortOrder: number;
    supersetSortOrder?: number;
    sectionId: string;
  };
  disabled?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, data, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    flex: 1,
    touchAction: 'none',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const Item = ({ exercise }: { exercise: ExerciseSectionEntry }) => {
  const { t } = useTranslation();
  const isSelectedExercise = checkIfSelectedExercise(exercise);

  return (
    <div
      key={exercise.workoutExerciseId}
      className={cn(
        'flex flex-row py-3 pl-4 gap-2 w-full border border-gray-200 rounded-md shadow-sm items-center',
        isSelectedExercise ? 'bg-white' : 'bg-gray-50',
      )}
    >
      {isSelectedExercise && (
        <MdDragIndicator className="text-black" size={24} />
      )}
      <Typography
        variant={isSelectedExercise ? 'body1Semibold' : 'body1'}
        className={isSelectedExercise ? 'text-black' : 'text-black/60'}
      >
        {getExerciseDisplay(exercise, t)}
      </Typography>
    </div>
  );
};
